import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { setSubpage } from '../../../routes/routerSlice';
import useAdministrationEntityLists from './useAdministrationEntityLists';
import { EntityListTypeSupported, getEntityListSpecification } from './entityListsLib';
import { setSelectedEntityList } from './entityListAdministrationSlice';
import EntityListCatalog from './catalog/EntityListCatalog';
import EntityListAdministrationContent from './content/EntityListAdministrationContent';
import { MainContentWrapper } from '@ydistri/ds';
import { ReduxState, useAppDispatch } from '../../../store';
import { useSelector } from 'react-redux';
import { useStableParams } from '../../../routes/useStableParams';

interface EntityListsAdministrationProps {
  entityListType: EntityListTypeSupported;
}

const EntityListsAdministration: React.FC<EntityListsAdministrationProps> = ({
  entityListType,
}) => {
  const { entityListId } = useStableParams();
  const dispatch = useAppDispatch();
  const { data: entityLists, fulfilledTimeStamp } = useAdministrationEntityLists(entityListType);
  const reconciledAt = useRef<number | undefined>(undefined);
  const selectedEntityList = useSelector(
    (state: ReduxState) => state.entityListsAdministration[entityListType].selectedEntityList,
  );

  useEffect(() => {
    dispatch(setSubpage(getEntityListSpecification(entityListType).subpage));
  }, [dispatch, entityListType]);

  useEffect(() => {
    if (entityListId && entityLists) {
      const requestedEntityListId = parseInt(entityListId);
      if (selectedEntityList?.entityListId !== requestedEntityListId) {
        const requestedEntityList = entityLists.find(
          el => el.entityListId === requestedEntityListId,
        );
        if (requestedEntityList) {
          dispatch(setSelectedEntityList({ entityListType, data: requestedEntityList }));
        }
      }
    }
  }, [dispatch, entityListId, entityLists, entityListType, selectedEntityList]);

  // Each load of the collection carries into the selection whether the list may be deleted, which its holders decide,
  // and drops a selection it no longer holds, such as a list whose data the entity-list cleanup deleted. Only that
  // flag is taken, so a load that started before a rename does not undo it; patches of the cache in between change
  // nothing, so a list selected before its notification arrives stays selected.
  useEffect(() => {
    if (
      !entityLists ||
      fulfilledTimeStamp === undefined ||
      reconciledAt.current === fulfilledTimeStamp
    )
      return;
    reconciledAt.current = fulfilledTimeStamp;
    if (!selectedEntityList) return;
    const loaded = entityLists.find(el => el.entityListId === selectedEntityList.entityListId);
    if (!loaded) {
      dispatch(setSelectedEntityList({ entityListType, data: undefined }));
    } else if (loaded.isDeletable !== selectedEntityList.isDeletable) {
      dispatch(
        setSelectedEntityList({
          entityListType,
          data: { ...selectedEntityList, isDeletable: loaded.isDeletable },
        }),
      );
    }
  }, [dispatch, entityLists, entityListType, fulfilledTimeStamp, selectedEntityList]);

  return (
    <>
      <Helmet title={getEntityListSpecification(entityListType).pageTitle} />
      <MainContentWrapper>
        <EntityListCatalog entityListType={entityListType} />
        <EntityListAdministrationContent entityListType={entityListType} />
      </MainContentWrapper>
    </>
  );
};

export default EntityListsAdministration;
